<template lang="pug">
v-container.pt-8.px-8(fluid)
  //- Top title
  v-row.mb-2(no-gutters align="center")
    v-col.shrink
      v-btn.ml-n3(text icon height, @click="goBack")
        v-icon(color="neutral700" :size="24") mdi-arrow-left
    v-col.ml-3
      .text-2xl.font-semibold.neutral700--text Stock Movement Report
  //- Actions
  v-row.mt-2(no-gutters)
    v-col(cols="12" md="8")
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center
        v-spacer
        v-btn(icon color="neutral500" @click="clickSearch")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        v-menu(offset-y left rounded="lg")
          template(v-slot:activator="{ on }")
            v-btn.button-sm.ml-4.white--text(v-on="on" color="neutral700" depressed :loading="isDownloading" :disabled="!canDownloadReport") Download
          v-list(dense)
            v-list-item-group(v-model="selectedDownloadType" @change="downloadReport")
              v-list-item(v-for="downloadType in downloadTypes" :key="downloadType" :value="downloadType")
                v-list-item-content
                  v-list-item-title.text-sm.neutral700--text {{ downloadType }}
  v-divider
  //- Filter
  stock-movement-filter-row.my-4(:filters.sync="filters" ref="filter")
  //- Listing
  .data-table.pb-4(v-if="hasAllRequiredFilters")
    v-data-table(:headers="headers" :items="stockMovements" item-key="product_id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" disable-sort loader-height="4")
      template(v-slot:item.product_name="{ item }")
        div {{ item.product_name }}
          span.error--text.ml-2(v-if="item.is_low_stock") (Low Stock)
      template(v-slot:item.quantity_in="{ item }")
        div.text-right {{ item.quantity_in | formatNumber }}
      template(v-slot:item.quantity_out="{ item }")
        div.text-right {{ item.quantity_out | formatNumber }}
      template(v-slot:item.change="{ item }")
        div.text-right {{ item.change | formatNumber }}
      template(v-slot:item.ending_balance="{ item }")
        div.text-right {{ item.ending_balance | formatNumber }}
      template(v-slot:item.cost="{ item }")
        div.text-right {{ item.cost | displayPriceRaw }}
      template(v-slot:item.total_value="{ item }")
        div.text-right {{ item.total_value | displayPriceRaw }}
      template(v-slot:item.actions="{ item }")
        v-btn.button-sm(text color="primary" small, @click="viewDetailedReport(item)") View Details
  //- No filters
  v-card.rounded-lg.border-dashed.pa-8.d-flex.flex-column.justify-center.align-center.no-filter-height(v-else outlined)
    v-sheet.round-full.pa-2.neutral300.d-flex.justify-center.align-center
      v-icon(size="22" color="neutral700") $yzi-file-search-02
    .text-md.mt-4.font-semibold Apply date range filter to display data
    .text-md.neutral500--text Apply filters located at upper left corner of the table
  v-footer.white.footer(v-if="hasAllRequiredFilters && totalCount > pageSize" app padless fixed)
    .d-flex.flex-column.grow.mb-2
      v-divider
      .d-flex.justify-end.mt-2.mx-4
        yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  //- Detailed Report Dialog
  v-dialog(v-model="detailDialog" max-width="900px" scrollable)
    v-card
      v-card-title.d-flex.align-center
        .text-xl.font-semibold Stock Movement Details
        v-spacer
        v-btn(icon @click="detailDialog = false")
          v-icon mdi-close
      v-divider
      v-card-text.pa-6(v-if="isLoadingDetail")
        .d-flex.justify-center.align-center.py-8
          v-progress-circular(indeterminate color="primary")
      v-card-text.pa-6(v-else-if="detailData")
        .mb-4
          .text-lg.font-semibold {{ selectedProduct.product_name }}
          .text-sm.neutral500--text SKU: {{ selectedProduct.product_sku }}

        .mb-6
          v-simple-table
            template(v-slot:default)
              thead
                tr
                  th.text-left Action
                  th.text-right Quantity
              tbody
                tr(v-for="action in detailData.actions" :key="action.action")
                  td {{ action.action_display }}
                  td.text-right {{ action.quantity | formatNumber }}
                tr.font-weight-bold
                  td Total
                  td.text-right {{ detailData.total_quantity | formatNumber }}

        .mb-4
          .text-md.font-semibold.mb-2 Summary
          v-row(dense)
            v-col(cols="6")
              .d-flex.justify-space-between
                span.neutral500--text Quantity In:
                span.font-semibold {{ selectedProduct.quantity_in | formatNumber }}
            v-col(cols="6")
              .d-flex.justify-space-between
                span.neutral500--text Quantity Out:
                span.font-semibold {{ selectedProduct.quantity_out | formatNumber }}
            v-col(cols="6")
              .d-flex.justify-space-between
                span.neutral500--text Net Change:
                span.font-semibold {{ selectedProduct.change | formatNumber }}
            v-col(cols="6")
              .d-flex.justify-space-between
                span.neutral500--text Ending Balance:
                span.font-semibold {{ selectedProduct.ending_balance | formatNumber }}
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import StockMovementFilterRow from '@/components/clinic/reports/filters/StockMovementFilterRow.vue'

export default {
  components: {
    StockMovementFilterRow
  },
  data() {
    return {
      searchText: null,
      showSearchField: false,
      filters: [],
      isFetching: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      stockMovements: [],
      isDownloading: false,
      selectedDownloadType: null,
      downloadTypes: ['pdf', 'csv'],
      detailDialog: false,
      selectedProduct: null,
      detailData: null,
      isLoadingDetail: false,
    }
  },
  watch: {
    filters: {
      handler() {
        if (this.hasAllRequiredFilters) {
          this.currentPage = 1
          this.fetchStockMovements()
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      isMobileScreen: 'ui/getIsMobileScreen'
    }),
    headers() {
      return [
        { text: 'Product Name', value: 'product_name', width: '200px' },
        { text: 'SKU', value: 'product_sku', width: '120px' },
        { text: 'Quantity In', value: 'quantity_in', width: '120px', align: 'right' },
        { text: 'Quantity Out', value: 'quantity_out', width: '120px', align: 'right' },
        { text: 'Net Change', value: 'change', width: '120px', align: 'right' },
        { text: 'Ending Balance', value: 'ending_balance', width: '140px', align: 'right' },
        { text: 'Cost', value: 'cost', width: '100px', align: 'right' },
        { text: 'Total Value', value: 'total_value', width: '120px', align: 'right' },
        { text: 'Actions', value: 'actions', width: '100px', align: 'center', sortable: false },
      ]
    },
    hasAllRequiredFilters() {
      return this.filters.some(f => f.key === 'date_range')
    },
    params() {
      let params = {
        company: this.company.id,
        page: this.currentPage,
        page_size: this.pageSize,
      }

      if (this.searchText) {
        params.search = this.searchText
      }

      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })

      return params
    },
    canDownloadReport() {
      return this.hasAllRequiredFilters && this.stockMovements.length > 0
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    goBack() {
      this.$router.push('/admin/clinic/settings')
    },
    clickSearch() {
      this.showSearchField = !this.showSearchField
      if (!this.showSearchField) {
        this.searchText = null
      }
    },
    blurSearch() {
      if (!this.searchText) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.fetchStockMovements()
    }, 750),
    async fetchStockMovements() {
      try {
        this.isFetching = true
        const url = '/product_inventories/stock_movement_report'
        const { data } = await this.$axios.get(url, { params: this.params })
        this.stockMovements = data.items || []
        this.totalCount = data.count || 0
      } catch (error) {
        console.error('Error fetching stock movements:', error)
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    pageChanged(num) {
      this.currentPage = num
      this.fetchStockMovements()
    },
    async downloadReport() {
      if (!this.selectedDownloadType) return

      this.isDownloading = true

      // Get OTK first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
        return
      }

      // Determine base URL by environment
      let baseUrl
      if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      } else {
        baseUrl = 'https://export-test.yezza.io'
      }

      // Construct URL
      let url = new URL(`${baseUrl}/product_inventories/stock_movement_report`)
      url.searchParams.set('key', key)

      // Add params (omit pagination params)
      const omitKeys = ['page', 'page_size']
      for (const objKey in this.params) {
        if (!omitKeys.includes(objKey) && this.params[objKey]) {
          url.searchParams.set(objKey, this.params[objKey])
        }
      }

      // Set export type
      url.searchParams.set('export', this.selectedDownloadType)

      // Open download
      window.open(url.toString(), this.selectedDownloadType === 'pdf' ? '_blank' : '_self')

      this.selectedDownloadType = null
      this.isDownloading = false
    },
    async viewDetailedReport(item) {
      try {
        this.selectedProduct = item
        this.detailDialog = true
        this.isLoadingDetail = true

        const params = {
          company: this.company.id,
          product: item.product_id,
        }

        // Add date range filters
        this.filters.forEach(filter => {
          if (filter.key === 'date_range' || filter.key === 'from_date' || filter.key === 'until_date') {
            params[filter.key] = filter.value
          }
        })

        const url = '/product_inventories/stock_movement_detail'
        const { data } = await this.$axios.get(url, { params })
        this.detailData = data
      } catch (error) {
        console.error('Error fetching detailed report:', error)
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingDetail = false
      }
    }
  }
}
</script>

<style scoped>
.no-filter-height {
  min-height: 400px;
}

.footer {
  z-index: 3;
}
</style>

